import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  html {
    scroll-behavior: smooth;
    font-size: 16px;
    line-height: 1.5;
  }
  
  body {
    font-family: ${props => props.theme.fonts.primary};
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
  }
  
  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.fonts.primary};
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.text};
    text-transform: uppercase;
    letter-spacing: -0.02em;
  }
  
  h1 {
    font-size: ${props => props.theme.fontSizes['8xl']};
    font-weight: 900;
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: ${props => props.theme.fontSizes['6xl']};
    }
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes['5xl']};
    }
  }
  
  h2 {
    font-size: ${props => props.theme.fontSizes['6xl']};
    font-weight: 800;
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: ${props => props.theme.fontSizes['5xl']};
    }
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes['4xl']};
    }
  }
  
  h3 {
    font-size: ${props => props.theme.fontSizes['4xl']};
    font-weight: 700;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes['3xl']};
    }
  }
  
  h4 {
    font-size: ${props => props.theme.fontSizes['2xl']};
    font-weight: 600;
  }
  
  h5 {
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: 600;
  }
  
  h6 {
    font-size: ${props => props.theme.fontSizes.lg};
    font-weight: 600;
  }
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.7;
    font-weight: 400;
  }
  
  /* Links */
  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    transition: color ${props => props.theme.transitions.fast};
    
    &:hover {
      color: ${props => props.theme.colors.accent};
    }
  }
  
  /* Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.sm};
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
    border: 2px solid transparent;
    border-radius: ${props => props.theme.borderRadius.base};
    font-family: ${props => props.theme.fonts.primary};
    font-weight: 700;
    font-size: ${props => props.theme.fontSizes.base};
    line-height: 1;
    cursor: pointer;
    transition: all ${props => props.theme.transitions.normal};
    text-decoration: none;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    &:focus {
      outline: 2px solid ${props => props.theme.colors.primary};
      outline-offset: 2px;
    }
  }
  
  .btn-primary {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    
    &:hover {
      background: ${props => props.theme.colors.accent};
      border-color: ${props => props.theme.colors.accent};
      transform: translateY(-2px);
    }
  }
  
  .btn-secondary {
    background: transparent;
    color: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    
    &:hover {
      background: ${props => props.theme.colors.primary};
      color: ${props => props.theme.colors.background};
      transform: translateY(-2px);
    }
  }
  
  .btn-ghost {
    background: transparent;
    color: ${props => props.theme.colors.text};
    border-color: ${props => props.theme.colors.divider};
    
    &:hover {
      background: ${props => props.theme.colors.surface};
      border-color: ${props => props.theme.colors.primary};
      color: ${props => props.theme.colors.primary};
    }
  }
  
  /* Cards */
  .card {
    background: ${props => props.theme.colors.surface};
    border: 1px solid ${props => props.theme.colors.divider};
    border-radius: ${props => props.theme.borderRadius.lg};
    padding: ${props => props.theme.spacing.xl};
    box-shadow: ${props => props.theme.shadows.cardShadow};
    transition: all ${props => props.theme.transitions.normal};
    
    &:hover {
      box-shadow: ${props => props.theme.shadows.hoverShadow};
      border-color: ${props => props.theme.colors.primary};
      transform: translateY(-4px);
    }
  }
  
  /* Layout */
  .container {
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 ${props => props.theme.spacing.xl};
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      padding: 0 ${props => props.theme.spacing.lg};
    }
  }
  
  .grid {
    display: grid;
    gap: ${props => props.theme.spacing.xl};
  }
  
  .grid-cols-1 { grid-template-columns: repeat(1, 1fr); }
  .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
  .grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
  .grid-cols-4 { grid-template-columns: repeat(4, 1fr); }
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    .grid-cols-4 { grid-template-columns: repeat(3, 1fr); }
    .grid-cols-3 { grid-template-columns: repeat(2, 1fr); }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    .grid-cols-4,
    .grid-cols-3,
    .grid-cols-2 { grid-template-columns: repeat(1, 1fr); }
  }
  
  /* Utilities */
  .flex { display: flex; }
  .flex-col { flex-direction: column; }
  .items-center { align-items: center; }
  .justify-center { justify-content: center; }
  .justify-between { justify-content: space-between; }
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .text-right { text-align: right; }
  
  /* Spacing */
  .mb-0 { margin-bottom: 0; }
  .mb-sm { margin-bottom: ${props => props.theme.spacing.sm}; }
  .mb-md { margin-bottom: ${props => props.theme.spacing.md}; }
  .mb-lg { margin-bottom: ${props => props.theme.spacing.lg}; }
  .mb-xl { margin-bottom: ${props => props.theme.spacing.xl}; }
  
  .mt-0 { margin-top: 0; }
  .mt-sm { margin-top: ${props => props.theme.spacing.sm}; }
  .mt-md { margin-top: ${props => props.theme.spacing.md}; }
  .mt-lg { margin-top: ${props => props.theme.spacing.lg}; }
  .mt-xl { margin-top: ${props => props.theme.spacing.xl}; }
  
  /* Text colors */
  .text-primary { color: ${props => props.theme.colors.primary}; }
  .text-secondary { color: ${props => props.theme.colors.textSecondary}; }
  .text-light { color: ${props => props.theme.colors.textLight}; }
  .text-accent { color: ${props => props.theme.colors.accent}; }
  
  /* Borders */
  .border { border: 1px solid ${props => props.theme.colors.divider}; }
  .border-t { border-top: 1px solid ${props => props.theme.colors.divider}; }
  .border-b { border-bottom: 1px solid ${props => props.theme.colors.divider}; }
  .border-l { border-left: 1px solid ${props => props.theme.colors.divider}; }
  .border-r { border-right: 1px solid ${props => props.theme.colors.divider}; }
  
  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.surface};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.divider};
    border-radius: ${props => props.theme.borderRadius.full};
    
    &:hover {
      background: ${props => props.theme.colors.primary};
    }
  }
  
  /* Selection */
  ::selection {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
  }
  
  /* Focus styles */
  *:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
  
  /* Form elements */
  input, textarea, select {
    font-family: inherit;
    font-size: 1rem;
    line-height: 1.5;
    color: ${props => props.theme.colors.text};
    background: ${props => props.theme.colors.surface};
    border: 2px solid ${props => props.theme.colors.divider};
    border-radius: ${props => props.theme.borderRadius.base};
    padding: ${props => props.theme.spacing.md};
    transition: border-color ${props => props.theme.transitions.normal};
    font-weight: 400;
    
    &:focus {
      border-color: ${props => props.theme.colors.primary};
      outline: none;
    }
    
    &::placeholder {
      color: ${props => props.theme.colors.textLight};
      font-weight: 400;
    }
  }
  
  /* Loading states */
  .loading {
    opacity: 0.6;
    pointer-events: none;
  }
  
  /* Animations */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(30px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  .fade-in {
    animation: fadeIn 0.6s ease-out;
  }
  
  .slide-up {
    animation: slideUp 0.8s ease-out;
  }
  
  .slide-in-left {
    animation: slideInLeft 0.8s ease-out;
  }
  
  .slide-in-right {
    animation: slideInRight 0.8s ease-out;
  }

  /* Modern magazine-style elements */
  .vertical-text {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    transform: rotate(180deg);
  }

  .large-number {
    font-size: ${props => props.theme.fontSizes['9xl']};
    font-weight: 900;
    line-height: 0.8;
    opacity: 0.1;
    position: absolute;
    z-index: 0;
  }

  .overlay-text {
    position: relative;
    z-index: 1;
  }
`;

export default GlobalStyles;